import { useMemo } from 'react';
import styled from '@emotion/styled';
import { alpha, Button, Drawer, DrawerProps, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { applyFilters, clearFilters } from '@/redux/slices/dataTableSlice';
import { selectDataTableFilters, selectIsFiltered } from '@/redux/selectors/dataTableSelectors';
import useConfirmationPrompt from '@/components/_hooks/useConfirmationPrompt';
import { deepEqual } from '@/_utils/utils';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import { SaveSearch } from '@/components/_common/Table';
import SavedSearches from '@/components/_common/Table/SavedSearches';

type FiltersDrawerProps = DrawerProps & {
  openFilterLayoutsModal: () => void;
  expandedFilters: string[];
  advanceFilters: Record<string, any>;
  onCollapseAll: () => void;
  onCancel: () => void;
};

const FiltersDrawer = ({
  openFilterLayoutsModal,
  expandedFilters,
  advanceFilters,
  children,
  onCollapseAll,
  onCancel,
  ...props
}: FiltersDrawerProps) => {
  const dispatch = useAppDispatch();
  const hasFilters = useAppSelector(selectIsFiltered);
  const appliedFilters = useAppSelector(selectDataTableFilters);
  const { dialog, dialogVisible, setDialogVisible } = useConfirmationPrompt({
    title: 'Unsaved Filters Changes',
    message: 'You have unsaved filters changes. Are you sure you want to discard them?',
    onConfirm: onCancel,
    onCancel: () => setDialogVisible(false)
  });
  const showCollapseButton = expandedFilters.length > 0;
  const hasChanges = useMemo(() => !deepEqual(advanceFilters, appliedFilters), [advanceFilters, appliedFilters]);

  const handleClearAllFilters = () => {
    dispatch(clearFilters(undefined));
  };

  const applyUpdatedFilters = () => {
    dispatch(applyFilters({ filters: advanceFilters }));
    onCancel();
  };

  const cancelChanges = () => {
    if (hasChanges) {
      setDialogVisible(true);
      return;
    }
    onCancel();
  };

  return (
    <>
      <StyledDrawer {...props} variant="persistent" anchor="left">
        <DrawerContentWrapper showCollapseButton={+showCollapseButton}>
          <DrawerHeader>
            <HeaderContainer>
              <Header>
                <HeaderText>Filters</HeaderText>
                {hasFilters && (
                  <ClearAllButton variant="text" onClick={handleClearAllFilters}>
                    Clear all
                  </ClearAllButton>
                )}
              </Header>
              <Stack direction="row" gap={1} alignItems="center">
                <Tooltip title="Edit filters">
                  <EditFiltersButton onClick={openFilterLayoutsModal}>
                    <PolarisIcon icon="settings" />
                  </EditFiltersButton>
                </Tooltip>
                <CloseButton onClick={cancelChanges}>
                  <PolarisIcon icon="close" />
                </CloseButton>
              </Stack>
            </HeaderContainer>
            <SaveSearch />
            <SavedSearches isOnDrawer />
          </DrawerHeader>
          <CollapseAllButton variant="text" onClick={onCollapseAll} sx={{ visibility: showCollapseButton ? 'visible' : 'hidden' }}>
            Collapse All
          </CollapseAllButton>
          <FiltersContainer>{children}</FiltersContainer>
          <DrawerAction direction="row" gap={2}>
            <CancelButton variant="text" onClick={cancelChanges}>
              Cancel
            </CancelButton>
            <ApplyButton variant="contained" disabled={!hasChanges} onClick={applyUpdatedFilters}>
              Apply
            </ApplyButton>
          </DrawerAction>
        </DrawerContentWrapper>
      </StyledDrawer>
      {dialogVisible && dialog}
    </>
  );
};

export default FiltersDrawer;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  padding: 0,
  '&.MuiDrawer-root > .MuiPaper-root': {
    width: '100%',
    transition: 'ease opacity 250ms',
    fontSize: 14,
    maxWidth: 370,
    background: theme.palette?.filters?.drawer || (theme.palette.mode === 'dark' ? '#131415' : '#ffffff'),
    borderRight: 0
  }
}));

const DrawerContentWrapper = styled.div(({ showCollapseButton }: { showCollapseButton: number }) => ({
  flex: 1,
  display: 'grid',
  gridTemplateRows: `minmax(0,auto) minmax(0,${showCollapseButton ? '52px' : '0'}) minmax(0,1fr) minmax(0,auto)`,
  height: '100%',
  transition: 'grid-template-rows 0.3s ease'
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),
  gap: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette?.filters?.drawer || (theme.palette.mode === 'dark' ? '#131415' : '#ffffff')}`
}));

const HeaderContainer = styled.div(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  paddingBottom: theme.spacing(1)
}));

const Header = styled.div(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  alignItems: 'baseline'
}));

const HeaderText = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  fontSize: theme.typography.pxToRem(23)
}));

const ClearAllButton = styled(Button)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 'semibold',
  minWidth: 0,
  padding: theme.spacing(0.25, 0.5)
}));

const EditFiltersButton = styled(IconButton)(() => ({
  width: 28,
  height: 28,
  padding: 0,
  '& svg': {
    width: 16,
    height: 16
  }
}));

const CloseButton = styled(IconButton)(() => ({
  width: 28,
  height: 28,
  padding: 0,
  '& svg': {
    width: 16,
    height: 16
  }
}));

const FiltersContainer = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  backgroundColor: theme.palette?.filters?.accordion?.background || (theme.palette.mode === 'dark' ? '#1A1B1F' : '#f5f5f5'),
  '& > *:not(:last-child)': {
    borderBottom: `3px solid ${theme.palette?.filters?.drawer || (theme.palette.mode === 'dark' ? '#131415' : '#ffffff')}`
  }
}));

const CollapseAllButton = styled(Button)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 'bold',
  width: 'fit-content',
  margin: theme.spacing(1, 2, 2, 'auto'),
  color: theme.palette?.filters?.collapse || theme.palette.tertiary.main,
  '&:hover': {
    backgroundColor: alpha(theme.palette?.filters?.collapse || theme.palette.tertiary.main, 0.08)
  }
}));

const DrawerAction = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(4, 2),
  borderTop: `2px solid ${theme.palette.background.default}`
}));

const ApplyButton = styled(Button)(({ theme }) => ({
  flex: 1,
  height: 36,
  fontWeight: theme.typography.fontWeightBold,
  boxShadow: 'none'
}));

const CancelButton = styled(Button)(({ theme }) => ({
  flex: 1,
  height: 36,
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette?.filters?.cancel || theme.palette.default.main,
  '&:hover': {
    backgroundColor: alpha(theme.palette?.filters?.cancel || theme.palette.default.main, 0.08)
  }
}));
